import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { planService } from '../../application/PlanService';
import { DateChartAnalytics } from '../../domain/analytics/DateChartAnalytics';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useRetrieveUser } from '../user/useRetrieveUser';

export interface CalendarDataProps {
  date: string;
  value: number;
}

type UseRetrieveAllProjectStopsResponse = ServiceResponse<null, CalendarDataProps[]> & {
  data: CalendarDataProps[];
};

// Hooks act as 'Adapter' layer.
export const useRetrievePlansCalendar = ({
  startDate,
  endDate,
}: {
  startDate: Date;
  endDate: Date;
}): UseRetrieveAllProjectStopsResponse => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: user } = useRetrieveUser();

  const [allPlansData, setCalendarData] = useState<CalendarDataProps[] | undefined>();

  const { isLoading } = useQuery(
    [`planner_project_plans_calendar`, startDate, endDate, activeProject!.id],
    () =>
      planService.getPlansCalendar({
        startDate,
        endDate,
        projectId: activeProject!.id,
      }),
    {
      enabled: !!user && !!activeProject,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (response: DateChartAnalytics[]) => {
        setCalendarData(
          response.map((p) => ({
            date: p.date,
            value: p.value,
          }))
        );
      },
      onError: (err: any) => {
        console.info(`Planner project all stops error`, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
      },
    }
  );

  return {
    error: false,
    isLoading: isLoading,
    success: false,
    run: null,
    data: allPlansData || [],
  };
};
